import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addDays, isWithinInterval } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import toast from 'react-hot-toast';
import {
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import UpdateIcon from '@mui/icons-material/Update';
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff"
import FlightLandIcon from "@mui/icons-material/FlightLand"
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { setFlights } from "state";
import { LAYOUT_HEIGHT, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import api from 'state/axios';
import { fetchFlights } from 'state/api';
import {
  removeItemByValueImmutable,
  filterFlights,
  sortMultipleFields,
} from 'state/helpers';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import FlightFilterDialog from 'components/FlightFilterDialog';
import FlightStepperDialog from "scenes/tatInstances/FlightStepperDialog";
import TaskTimeline from "./TaskTimeline"


const formatTZDate = (date, tz = "UTC", fmt = "HH:mm dd/MM/yy") => {
  if (!date) return "";
  try { return formatInTimeZone(date, tz, fmt); } catch { return ""; }
};

const buildTasksFromTemplate = (template) =>
  template?.tasks?.map((t) => ({
    taskCode: t.taskCode,
    taskName: t.taskName,
    preceding: t.preceding,
    startTime: t.startTime || 0,
    endTime: t.endTime || 0,
    duration: t.duration || 0,
    priority: t.priority || 0,
    actualStartDate: null,
    actualEndDate: null,
    actualDuration: null,
    remarks: "",
    status: "pending",
    assignedRole: "Unassigned",
  })) || [];

const pickBestTemplate = (templates, flight) => {
  let best = null;
  let bestScore = -1;
  for (const t of templates) {
    let score = 0;
    if (!t.acTypeCodes?.length || t.acTypeCodes.includes(flight.acType)) score++;
    if (!t.acRegs?.length || t.acRegs.includes(flight.acReg)) score++;
    if (!t.airportCodes?.length || t.airportCodes.includes(flight.dep)) score++;
    if (!t.sectors?.length || t.sectors.includes(flight.sector)) score++;
    if (score > bestScore) { 
      bestScore = score; 
      best = t; 
    }
  }
  return best;
};


const TATController = () => {
  useAuthCheck();
  const dispatch = useDispatch();
  const timeMode = useSelector((state) => state.timemode);
  const flightsData = useSelector((state) => state.flights);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const selectedFlightRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [openStepper, setOpenStepper] = useState(false);
  const [sortOption, setSortOption] = useState('none');
  const [port, setPort] = useState('ALL');
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [tatTemplates, setTatTemplates] = useState([]);
  const [tatInstances, setTatInstances] = useState([]);
  // Optimistic progress patches keyed by `${flightDate}-${flightDesc}-${dep}`
  const [progressOverrides, setProgressOverrides] = useState(new Map());

  // Keep ref in sync so handleProgressUpdate stays stable (no dep on selectedFlight)
  selectedFlightRef.current = selectedFlight;

  useEffect(() => {
    if (!startDate || !endDate) return;

    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);

        const [tatTemplatesData, tatInstancesData] = await Promise.all([
          api.get('/tattemplates/active', {
            signal: controller.signal,
          }).then(r => r.data),

          api.post('/tatinstances', {
            startDate: addDays(startDate, -1),
            endDate: addDays(endDate, 1),
          }, {
            signal: controller.signal,
          }).then(r => r.data),
        ]);

        setTatTemplates(tatTemplatesData || []);
        setTatInstances(tatInstancesData || []);
        setProgressOverrides(new Map());
      } catch (err) {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          return;
        }

        setError(err);
        toast.error(err?.message || 'Failed to fetch data!');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [startDate, endDate]);

  // O(1) lookup — keyed by `${flightDate.getTime()}-${flightDesc}-${dep}`
  const instanceMap = useMemo(() => {
    const map = new Map();
    for (const i of tatInstances) {
      const key = `${new Date(i.flightDate).getTime()}-${i.flightDesc}-${i.dep}`;
      map.set(key, i);
    }
    return map;
  }, [tatInstances]);

  // Derived synchronously — no extra render cycle, no spurious loading state
  const processedFlights = useMemo(() => {
    const flights = removeItemByValueImmutable(
      flightsData, 
      "flightStatus", 
      EXCLUDED_FLIGHT_STATUS
    );

    const filtered = filterFlights({ flights, filters, timeMode });
    const sorted = sortMultipleFields(filtered, [
      { field: "fullEtd", direction: "asc" },
      { field: "flightReg", direction: "asc" },
    ]);

    return sorted.map((f) => {
      const key = `${new Date(f.flightDate).getTime()}-${f.flightDesc}-${f.dep}`;
      const override = progressOverrides.get(key);
      const matchedInstance = instanceMap.get(key);

      let result;
      if (matchedInstance) {
        const total = matchedInstance.tasks.length;
        const done = matchedInstance.tasks.filter(t => t.status === "completed").length;
        result = {
          ...f,
          tasks: matchedInstance.tasks || [],
          templateId: matchedInstance.templateId || null,
          instanceId: matchedInstance._id,
          isCompleted: matchedInstance.isCompleted,
          progress: total ? Math.round((done / total) * 100) : 0,
        };
      } else {
        const bestTemplate = pickBestTemplate(tatTemplates, f);
        result = {
          ...f,
          tasks: buildTasksFromTemplate(bestTemplate),
          templateId: bestTemplate?._id || null,
          progress: 0,
        };
      }

      return override ? { ...result, ...override } : result;
    });
  }, [
    timeMode, 
    flightsData, 
    filters, 
    instanceMap, 
    tatTemplates, 
    progressOverrides,
  ]);

  const sortedFlights = useMemo(() => {
    if (!processedFlights.length) return [];

    const data = port !== "ALL"
      ? processedFlights.filter(f => f?.dep === port)
      : processedFlights;

    if (sortOption === "none") return data;

    // Pre-compute timestamps to avoid repeated Date.parse in comparator
    const timestamps = new Map(data.map(f => [f._id, f.fullEtd ? Date.parse(f.fullEtd) : null]));

    return [...data].sort((a, b) => {
      const regCmp = a.flightReg.localeCompare(b.flightReg);
      if (regCmp !== 0) return regCmp;
      const ta = timestamps.get(a._id);
      const tb = timestamps.get(b._id);
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return ta - tb;
    });
  }, [processedFlights, sortOption, port]);

  const {
    acTypeOptions,
    flightRegOptions,
    sectorTypeOptions,
    departureTypeOptions,
    arrivalTypeOptions,
    airportOptions,
  } = useMemo(() => {
    if (!processedFlights.length) {
      return {
        acTypeOptions: [],
        flightRegOptions: [],
        sectorTypeOptions: [],
        departureTypeOptions: [],
        arrivalTypeOptions: [],
        airportOptions: [],
      };
    }

    const sets = {
      acType: new Set(),
      flightReg: new Set(),
      sectorType: new Set(),
      departureType: new Set(),
      arrivalType: new Set(),
      airport: new Set(),
    };

    for (const { acType, flightReg, sectorType, departureType, arrivalType, dep, arr } of processedFlights) {
      if (acType) sets.acType.add(acType);
      if (flightReg) sets.flightReg.add(flightReg);
      if (sectorType) sets.sectorType.add(sectorType);
      if (departureType) sets.departureType.add(departureType);
      if (arrivalType) sets.arrivalType.add(arrivalType);
      if (dep) sets.airport.add(dep);
      if (arr) sets.airport.add(arr);
    }

    const toSortedArray = (set) => [...set].sort();
    return {
      acTypeOptions: toSortedArray(sets.acType),
      flightRegOptions: toSortedArray(sets.flightReg),
      sectorTypeOptions: toSortedArray(sets.sectorType),
      departureTypeOptions: toSortedArray(sets.departureType),
      arrivalTypeOptions: toSortedArray(sets.arrivalType),
      airportOptions: toSortedArray(sets.airport),
    };
  }, [processedFlights]);

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  const handleFilterApply = (filterValues) => setFilters(filterValues);

  const handleSortToggle = () => {
    setSortOption(prev => prev === 'none' ? 'flightReg_fullEtd' : 'none');
  };

  const handleOpenStepper = (flight) => {
    setSelectedFlight(flight);
    setOpenStepper(true);
  };

  const handleCloseStepper = () => {
    setSelectedFlight(null);
    setOpenStepper(false);
  };

  // Stable callback — reads selectedFlight via ref to avoid re-creating on every flight selection
  const handleProgressUpdate = useCallback((instanceId, progress, flightInfo) => {
    const target = flightInfo || selectedFlightRef.current;
    if (!target) return;
    const key = `${new Date(target.flightDate).getTime()}-${target.flightDesc}-${target.dep}`;
    setProgressOverrides(prev => {
      const next = new Map(prev);
      next.set(key, flightInfo ? { instanceId, progress } : { instanceId: null, progress: 0 });
      return next;
    });
  }, []);

  const handleFlights = async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      const result = await fetchFlights(addDays(startDate, -1), addDays(endDate, 1));
      if (result.length > 0) {
        const flights = result.filter(f => {
          const flightDateToCompare = timeMode === 'UTC' ? f.flightDate : f.flightDateLocalBase;
          return isWithinInterval(flightDateToCompare, { start: startDate, end: endDate });
        });
        dispatch(setFlights({ flights }));
      }
      toast.success("Refresh flights successfully!");
    } catch (error) {
      setError(error)
      toast.error(error?.message || "Failed to fetch flights!");
    } finally {
      setLoading(false);
    }    
  };


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="TAT Controller" 
          subtitle="Tracking Board" 
        />
        <Box display="flex" alignItems="center" gap={1}>
          <FormControl>
            <InputLabel>Port</InputLabel>
            <Select
              size="small"
              label="Port"
              value={port || ''}
              onChange={(e) => setPort(e.target.value)}
              sx={{ width: isMobile ? '5rem' : '6rem' }}
            >
              <MenuItem value='ALL'>ALL</MenuItem>
              {airportOptions.map((airport) => (
                <MenuItem key={airport} value={airport}>{airport}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={`Sort by ${sortOption === 'flightReg_fullEtd' ? 'Reg + Etd' : 'Etd'}`} arrow>
            <span>
              <StyledButton
                onClick={handleSortToggle}
                sx={{
                  color: sortOption === 'none'
                    ? `${theme.palette.text.primary} !important`
                    : `${theme.palette.primary.dark} !important`,
                }}
              >
                <SortIcon />
              </StyledButton>
            </span>
          </Tooltip>

          {!isMobile && (
            <Tooltip title="Refresh" arrow>
              <span>
                <StyledButton onClick={handleFlights}>
                  <UpdateIcon />
                </StyledButton>
              </span>
            </Tooltip>
          )}

          <FilterButton filters={filters} onClick={handleFilterOpen} />
        </Box>
      </FlexBetween>

      {error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          position="relative"
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (t) => t.palette.mode === 'dark'
                  ? 'rgba(0,0,0,0.45)'
                  : 'rgba(255,255,255,0.6)',
                borderRadius: 2,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            <Grid spacing={3}>
              {sortedFlights.map((f, idx) => {
                const prevTa = timeMode === "UTC"
                  ? formatTZDate(f?.prevFullAta || f?.prevFullEta)
                  : formatTZDate(f?.prevFullAtaLocalBase || f?.prevFullEtaLocalBase);

                const td = timeMode === "UTC"
                  ? formatTZDate(f?.fullAtd || f?.fullEtd)
                  : formatTZDate(f?.fullAtdLocalBase || f?.fullEtdLocalBase);

                return (
                  <Grid
                    key={f._id}
                    item xs={12}
                    mb={2}
                    borderRadius={2}
                    border={`1px solid ${theme.palette.primary.main}`}
                  >
                    <Card sx={{ borderRadius: 2, overflow: "hidden" }}>
                      <CardHeader
                        title={
                          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <Chip
                              label={idx + 1}
                              variant="outlined"
                              size="small"
                              sx={{ fontSize: "0.75rem", borderWidth: 1.5, height: 24, color: "neutral.light" }}
                            />
                            <Typography variant="h5" sx={{ fontWeight: "bold", color: "black" }}>
                              {f?.flightDesc}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: "bold", color: "black" }}>
                              {f?.sector}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: "bold", color: "black" }}>
                              {timeMode === 'UTC'
                                ? formatInTimeZone(f?.fullEtd, "UTC", 'dd/MM/yyyy')
                                : formatInTimeZone(f?.fullEtdLocalBase, "UTC", 'dd/MM/yyyy')
                              }
                            </Typography>
                            <Chip
                              label={f?.flightReg}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem", borderWidth: 1.5, height: 24, color: "black" }}
                            />
                          </Box>
                        }
                        subheader={
                          <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FlightLandIcon sx={{ color: "#ff6b35", fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: "black" }}>Landing: {prevTa}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FlightTakeoffIcon sx={{ color: "#ff6b35", fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: "black" }}>Departure: {td}</Typography>
                            </Box>
                          </Box>
                        }
                        sx={{ pb: 2, background: "linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)" }}
                      />
                      <CardContent>
                        <TaskTimeline tasks={f?.tasks} onEdit={() => handleOpenStepper(f)} />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      <FlightStepperDialog
        open={openStepper}
        onClose={handleCloseStepper}
        flight={selectedFlight}
        onProgressUpdate={handleProgressUpdate}
      />

      <ScrollToTop />
      {isFilterDialogOpen &&
        <FlightFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          acTypeOptions={acTypeOptions}
          flightRegOptions={flightRegOptions}
          sectorTypeOptions={sectorTypeOptions}
          departureTypeOptions={departureTypeOptions}
          arrivalTypeOptions={arrivalTypeOptions}
        />
      }
    </Box>
  );
};

export default TATController;
